/* eslint-disable sort-keys */

/**
 * Used to translate step names to an integer
 */
export const Steps = {
	RoomCreation: 0,
	Indoor: 1,
	Outdoor: 2,
	RequiredProducts: 3,
	Upsell: 4,
	Review: 5
};

/**
 * Used to translate state action names to an integer
 */
export const StateActions = {
	StepUpdate: 0,
	RoomCountUpdate: 1,
	RoomsUpdate: 2,
	RoomUpdate: 3,
	RoomNameUpdate: 4,
	RoomSelectionUpdate: 5,
	RoomProductUpdate: 6,
	SelectedRoomUpdate: 7,
	UpsellProductsUpdate: 8,
	UpsellProductCodeUpdate: 9,
	UpsellProductQuantityUpdate: 10,
	UpsellProductAdd: 11,
	UpsellProductRemove: 12,
	RequiredProductsUpdate: 13,
	RequiredProductCodeUpdate: 14,
	RequiredProductQuantityUpdate: 15,
	RequiredProductAdd: 16,
	RequiredProductRemove: 17,
	RequiredProductsCount: 18,
	OutdoorProductUpdate: 19
};
/* eslint-enable sort-keys */

/**
 * Used as the default value for state if no url parameter is found.
 *
 * @type {import('../types').MultiSystemState}
 */
export const DEFAULT_MULTI_SYSTEM_STATE = {
	step: Steps.RoomCreation
};

/**
 * The query parameter key used to retreive a state by it's id.
 *
 * @type {string}
 */
export const STATE_ID_QUERY_PARAM = 'state_id';

/**
 * The minimum number of rooms available for selection.
 *
 * @type {number}
 */
export const ROOM_MIN_COUNT = 2;

/**
 * The maximum number of rooms available for selection.
 *
 * @type {number}
 */
export const ROOM_MAX_COUNT = 5;

/**
 * The aviable capacities used for selection in a room.
 *
 * @type {import('../types').RoomCapacity[]}
 */
export const ROOM_CAPACITIES = [
	{
		class: ['7'],
		display: '2.00'
	},
	{
		class: ['9'],

		display: '2.50'
	},
	{
		class: ['12'],
		display: '3.50'
	},
	{
		class: ['14'],
		display: '4.00'
	},
	{
		class: ['18'],
		display: '5.00'
	},
	{
		class: ['22'],
		display: '6.00'
	},
	{
		class: ['24'],
		display: '7.00'
	}
];

/**
 * The unit displayed next to each capacity.
 *
 * @type {string}
 */
export const ROOM_CAPACITY_UNIT = 'kW';

/**
 * The facet field used to filter products by capacity.
 *
 * @type {string}
 */
export const CAPACITY_FILTER_FIELD = 'CapacityCoolDisplaykW';

/**
 * The default facets used when searching for indoor units.
 *
 * @type {import('extensions/ui-controllers/product-list/lib/product-list').MivaJsonApiFilter[]}
 */
export const INDOOR_UNIT_DEFAULT_FILTERS = [
	{
		name: 'search',
		value: [
			{
				field: 'CustomField_Values:customfields:IsFinishedGoods',
				operator: 'EQ',
				value: '1'
			}
		]
	},
	{
		name: 'search',
		value: [
			{
				field: 'CustomField_Values:customfields:IsIndoor',
				operator: 'EQ',
				value: '1'
			}
		]
	},
	{
		name: 'search',
		value: [
			{
				field: 'CustomField_Values:customfields:IsMulti',
				operator: 'EQ',
				value: '1'
			}
		]
	}
];

/**
 * The default facets used when searching for outdoor units.
 *
 * @type {import('extensions/ui-controllers/product-list/lib/product-list').MivaJsonApiFilter[]}
 */
export const OUTDOOR_UNIT_DEFAULT_FILTERS = [
	{
		name: 'search',
		value: [
			{
				field: 'CustomField_Values:customfields:IsFinishedGoods',
				operator: 'EQ',
				value: '1'
			}
		]
	},
	{
		name: 'search',
		value: [
			{
				field: 'CustomField_Values:customfields:IsOutdoor',
				operator: 'EQ',
				value: '1'
			}
		]
	},
	{
		name: 'search',
		value: [
			{
				field: 'CustomField_Values:customfields:IsMulti',
				operator: 'EQ',
				value: '1'
			}
		]
	}
];

/**
 * The default facets used when searching for upsell products.
 *
 * @type {import('extensions/ui-controllers/product-list/lib/product-list').MivaJsonApiFilter[]}
 */
export const UPSELL_DEFAULT_FILTERS = [
	{
		name: 'search',
		value: [
			{
				field: 'CustomField_Values:customfields:IsFinishedGoods',
				operator: 'EQ',
				value: '1'
			}
		]
	},
	{
		name: 'search',
		value: [
			{
				field: 'CustomField_Values:customfields:NotPurchasable',
				operator: 'EQ',
				value: '0'
			}
		]
	},
	{
		name: 'search',
		value: [
			{
				field: 'CustomField_Values:customfields:NotVisible',
				operator: 'EQ',
				value: '0'
			}
		]
	}
];

/**
 * The default facets used when searching for required products.
 *
 * @type {import('extensions/ui-controllers/product-list/lib/product-list').MivaJsonApiFilter[]}
 */
export const REQUIRED_PRODUCTS_DEFAULT_FILTERS = [
	{
		name: 'search',
		value: [
			{
				field: 'CustomField_Values:customfields:IsFinishedGoods',
				operator: 'EQ',
				value: '1'
			}
		]
	},
	{
		name: 'search',
		value: [
			{
				field: 'CustomField_Values:customfields:NotPurchasable',
				operator: 'EQ',
				value: '0'
			}
		]
	},
	{
		name: 'search',
		value: [
			{
				field: 'CustomField_Values:customfields:NotVisible',
				operator: 'EQ',
				value: '0'
			}
		]
	}
];

/**
 * The image type used to represnet the product in a product listing.
 *
 * @type {string}
 */
export const IMAGE_TYPE = 'main';

/**
 * The height of images in a product listing.
 *
 * @type {number}
 */
export const IMAGE_HEIGHT = 360;

/**
 * The width of images in a product listing.
 *
 * @type {number}
 */
export const IMAGE_WIDTH = 360;

/**
 * Formatted value of HEIGHTxWIDTH. Used in product listing queries.
 *
 * @type {number}
 */
export const IMAGE_SIZE = `${IMAGE_HEIGHT}x${IMAGE_WIDTH}`;

/**
 * ONDEMANDCOLUMNS used in product listing queries.
 *
 * @type {string[]}
 */
export const PRODUCT_LISTING_ONDEMANDCOLUMNS = [
	'sale_price',
	'CustomField_Values:customfields:CapacityCoolDisplaykW',
	'CustomField_Values:customfields:CapacityHeatDisplaykW',
	'CustomField_Values:customfields:IsFinishedGoods',
	'CustomField_Values:customfields:FGInventory',
	'CustomField_Values:customfields:IsIndoor',
	'CustomField_Values:customfields:CapacityClass',
	'CustomField_Values:customfields:category_facet'
];

/**
 * @type {import('../types').CategoryFilter[]}
 */
export const INDOOR_CATEGORY_FILTERS = [
	{
		code: 'CASSETTE',
		prompt: 'Cassette'
	},
	{
		code: 'DUCTED',
		prompt: 'Ducted'
	},
	{
		code: 'WALLMOUNTED',
		prompt: 'Wall Mounted'
	},
	{
		code: 'CONSOLE',
		prompt: 'Console'
	}
];
